import { useEffect } from 'react';
import HeapUtils from 'app/utils/heap';
import { ChecklistTaskState } from 'app/modules/onboardingChecklist/types';
import {
  CHECKLIST_ITEM_COMPLETED_EVENTS,
  CHECKLIST_TRACKING_IDS,
  ChecklistCompletionState,
  getChecklistCompletionUpdate,
  getDaysSinceSignup,
} from 'app/modules/onboardingChecklist/utils/checklistCompletionTracking';
import { useAccount } from 'modules/account/accountSelector';
import { usePage } from 'modules/page/pageSelector';
import { useUser } from 'modules/user/userSelector';
import { BrowserStorage } from 'utils/browserStorage';
import Logger from 'utils/logger';

const ALL_TRACKING_IDS = Object.values(CHECKLIST_TRACKING_IDS);

const getStorageKey = (accountId: number) =>
  `${BrowserStorage.keys.OnboardingChecklist.CompletionTracking}:${accountId}`;

const readState = (key: string): ChecklistCompletionState | null => {
  try {
    const value = BrowserStorage.get(key);

    return value ? (JSON.parse(value) as ChecklistCompletionState) : null;
  } catch (error) {
    Logger.error(error);
    return null;
  }
};

export const useChecklistCompletionTracking = (tasks: ChecklistTaskState[]) => {
  const { accountId } = useAccount();
  const { regDate } = useUser();
  const { hasLoaded: hasLoadedPages } = usePage();
  const doneIdsKey = tasks
    .filter((task) => task.isDone && CHECKLIST_TRACKING_IDS[task.id])
    .map((task) => CHECKLIST_TRACKING_IDS[task.id])
    .sort()
    .join(',');

  useEffect(() => {
    if (!hasLoadedPages || accountId == null) {
      return;
    }

    const storageKey = getStorageKey(accountId);
    const update = getChecklistCompletionUpdate(
      readState(storageKey),
      doneIdsKey ? doneIdsKey.split(',') : [],
      ALL_TRACKING_IDS,
    );

    BrowserStorage.set(storageKey, JSON.stringify(update.state));

    update.newlyCompletedIds.forEach((id) =>
      HeapUtils.track(CHECKLIST_ITEM_COMPLETED_EVENTS[id]),
    );

    if (update.isChecklistNewlyCompleted) {
      HeapUtils.track(HeapUtils.events.CHECKLIST_COMPLETED, {
        days_since_signup: getDaysSinceSignup(regDate),
      });
    }
  }, [accountId, doneIdsKey, hasLoadedPages, regDate]);
};
